/** Sign-in route for organizer auth. */
export const ORGANIZER_SIGN_IN_PATH = "/organizer/sign-in";

const MAX_RETURN_PATH_LEN = 2048;
const CONTROL_CHARS = /[\x00-\x1f\x7f]/;

/**
 * Mirrors server **`sanitize_organizer_return_path`** (DD-SRS-F-ORGANIZER-LOGIN-NEXT.3).
 */
export function sanitizeOrganizerReturnPath(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) {
    return "/";
  }
  const text = raw.trim();
  if (text === "") {
    return "/";
  }
  if (text.length > MAX_RETURN_PATH_LEN) {
    return "/";
  }
  if (CONTROL_CHARS.test(text)) {
    return "/";
  }
  if (text.includes("\\")) {
    return "/";
  }
  if (text.startsWith("//")) {
    return "/";
  }
  const lower = text.toLowerCase();
  if (lower.startsWith("http:") || lower.startsWith("https:")) {
    return "/";
  }

  let candidate = text;
  if (!candidate.startsWith("/")) {
    try {
      const parsed = new URL(candidate, "https://placeholder.invalid");
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
        return "/";
      }
      if (parsed.hostname !== "placeholder.invalid") {
        return "/";
      }
      candidate = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      if (!candidate.startsWith("/")) {
        return "/";
      }
    } catch {
      return "/";
    }
  }

  const hashIdx = candidate.indexOf("#");
  const beforeHash = hashIdx >= 0 ? candidate.slice(0, hashIdx) : candidate;
  const fragment = hashIdx >= 0 ? candidate.slice(hashIdx) : "";

  const queryIdx = beforeHash.indexOf("?");
  const pathOnly =
    queryIdx >= 0 ? beforeHash.slice(0, queryIdx) : beforeHash;
  const queryPart = queryIdx >= 0 ? beforeHash.slice(queryIdx) : "";

  const collapsed = collapsePathUnderRoot(pathOnly);
  if (collapsed === null) {
    return "/";
  }

  const out = `${collapsed}${queryPart}${fragment}`;
  if (out.length > MAX_RETURN_PATH_LEN) {
    return "/";
  }
  return out;
}

function collapsePathUnderRoot(pathPart: string): string | null {
  const normalized = pathPart.startsWith("/") ? pathPart : `/${pathPart}`;
  const segments = normalized.split("/").filter((s) => s !== "" && s !== ".");
  const stack: string[] = [];
  for (const seg of segments) {
    if (seg === "..") {
      if (stack.length === 0) {
        return null;
      }
      stack.pop();
      continue;
    }
    stack.push(seg);
  }
  if (stack.length === 0) {
    return "/";
  }
  return `/${stack.join("/")}`;
}

/** Current document path suitable for **`next`** (path + search + hash). */
export function currentOrganizerReturnPath(): string {
  if (typeof window === "undefined") {
    return "/";
  }
  return sanitizeOrganizerReturnPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

/** Whether the user is already on the sign-in shell (avoid redirect loops). */
export function isOnOrganizerSignInPage(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === ORGANIZER_SIGN_IN_PATH;
}

/**
 * Builds **`/organizer/sign-in`** with optional **`next`** query when not root.
 *
 * @param nextRelative - Sanitized return path; defaults to **`/`** (omit query param).
 */
export function organizerSignInPath(nextRelative?: string): string {
  const next = sanitizeOrganizerReturnPath(nextRelative ?? "/");
  if (next === "/") {
    return ORGANIZER_SIGN_IN_PATH;
  }
  const params = new URLSearchParams();
  params.set("next", next);
  return `${ORGANIZER_SIGN_IN_PATH}?${params.toString()}`;
}

/** Reads and sanitizes **`next`** from the current sign-in URL. */
export function readOrganizerReturnPathFromLocation(): string {
  if (typeof window === "undefined") {
    return "/";
  }
  const raw = new URLSearchParams(window.location.search).get("next");
  return sanitizeOrganizerReturnPath(raw);
}

/** Appends **`next`** to an API login start URL when return path is not root. */
export function appendNextQueryToLoginUrl(
  loginPath: string,
  nextRelative: string,
): string {
  const next = sanitizeOrganizerReturnPath(nextRelative);
  if (next === "/") {
    return loginPath;
  }
  const sep = loginPath.includes("?") ? "&" : "?";
  return `${loginPath}${sep}next=${encodeURIComponent(next)}`;
}
